import { useEffect, useRef, useState } from "react"
import { CoverTextElementData } from "./types"

const cleanColor = (color?: string) => (color ? color.replace("#FF", "#") : "#ffffff")

const buildSlotStyle = (layout: CoverTextElementData["layout"]) => {
  const widthPercentage = (layout.width / layout.layoutWidth) * 100
  const heightPercentage = (layout.height / layout.layoutHeight) * 100
  const normalizedLeft = ((layout.x - layout.width / 2) / layout.layoutWidth) * 100
  const normalizedTop = ((layout.y - layout.height / 2) / layout.layoutHeight) * 100

  return {
    width: `${widthPercentage}%`,
    height: `${heightPercentage}%`,
    left: `${normalizedLeft}%`,
    top: `${normalizedTop}%`,
    position: "absolute" as const,
  }
}

interface CoverTextElementProps {
  element: CoverTextElementData
  albumDirection?: string
}

export const CoverTextElement: React.FC<CoverTextElementProps> = ({
  element,
  albumDirection = "RTL",
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [fontSize, setFontSize] = useState("1rem")

  useEffect(() => {
    const calculateFontSize = () => {
      const containerHeight = containerRef.current?.offsetHeight ?? 0
      if (!containerHeight) return

      const baseSize = element.fontSizePx
        ? (element.fontSizePx / (element.layout.layoutHeight || 1)) * containerHeight
        : containerHeight * 0.6

      const scaleFactor = albumDirection === "RTL" ? 0.9 : 1
      setFontSize(`${baseSize * scaleFactor}px`)
    }

    calculateFontSize()
    const observer = new ResizeObserver(calculateFontSize)
    const node = containerRef.current
    if (node) observer.observe(node)

    return () => observer.disconnect()
  }, [albumDirection, element.fontSizePx, element.layout.layoutHeight])

  const opacityHex = Math.round((element.opacity ?? 1) * 255)
    .toString(16)
    .padStart(2, "0")

  return (
    <div
      ref={containerRef}
      className="absolute flex items-center justify-center"
      style={{
        ...buildSlotStyle(element.layout),
        backgroundColor: `${cleanColor(element.frameColor)}${opacityHex}`,
        paddingLeft: "2%",
        paddingRight: "2%",
      }}
    >
      <div
        style={{
          color: cleanColor(element.textColor),
          fontSize,
          lineHeight: fontSize,
          direction: element.direction === "RTL" ? "rtl" : "ltr",
          fontFamily: "LUPASANS-REGULAR",
          whiteSpace: "nowrap",
        }}
      >
        {element.text}
      </div>
    </div>
  )
}
